"use client";

import React from "react";

import Link from "next/link";

import { Card, CardBody } from "@heroui/card";
import { ArrowRight, Heart, MapPin } from "lucide-react";

import {
  COMPONENT_SIZES,
  SPACING,
  TYPOGRAPHY,
} from "@/lib/constants/layoutConstants";
import { OrganizationTerminology } from "@/lib/services/terminologyService";
import { cn } from "@/lib/utils";

interface AreaCardProps {
  area: {
    id: string;
    name: string;
    state: string;
    slug: string;
    description?: string | null;
    _count?: {
      needs?: number;
    };
  };
  organizationSlug: string;
  terminology: OrganizationTerminology;
}

/**
 * AreaCard Component
 *
 * A visually appealing card component for displaying area information
 * with proper accessibility and hover states
 */
function formatNeedsLabel(
  count: number,
  terminology: OrganizationTerminology,
): string {
  const term =
    count === 1 ? terminology.need.singular : terminology.need.plural;

  return `${terminology.termActive.toLowerCase()} ${term.toLowerCase()}`;
}

export function AreaCard({
  area,
  organizationSlug,
  terminology,
}: AreaCardProps) {
  const activeNeedsCount = area._count?.needs ?? 0;
  const areaUrl = `/${organizationSlug}/areas/${area.slug}`;
  const areaId = `area-${area.id}`;

  return (
    <article className="h-full" id={areaId}>
      <Link href={areaUrl}>
        <Card
          aria-labelledby={`${areaId}-title`}
          className={cn(
            "h-full overflow-hidden",
            "hover:shadow-xl hover:-translate-y-1",
            "transition-all duration-300",
            "cursor-pointer group",
            "border border-default-200",
            "bg-white",
            "focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2",
          )}
        >
          {/* Top accent bar */}
          <div className="h-1 bg-gradient-to-r from-primary-400 via-primary-500 to-primary-600" />

          <CardBody className={SPACING.CARD_PADDING_MD}>
            {/* Header with Icon and Arrow */}
            <div className="flex items-start justify-between mb-4">
              <div
                className={cn(
                  "flex items-center flex-1 min-w-0",
                  SPACING.BUTTON_GROUP_LARGE,
                )}
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-primary-50 to-primary-100 group-hover:from-primary-100 group-hover:to-primary-200 transition-all shadow-sm">
                  <MapPin
                    className={cn(COMPONENT_SIZES.ICON_MD, "text-primary-600")}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3
                    className={cn(
                      "text-lg font-semibold",
                      "group-hover:text-primary transition-colors",
                      "mb-1",
                    )}
                    id={`${areaId}-title`}
                  >
                    {area.name}
                  </h3>
                  <p
                    className={cn(
                      TYPOGRAPHY.SMALL,
                      "text-default-500 font-medium",
                    )}
                  >
                    {area.state}
                  </p>
                </div>
              </div>
              <ArrowRight
                className={cn(
                  COMPONENT_SIZES.ICON_SM,
                  "text-default-400 group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0 mt-1",
                )}
              />
            </div>

            {/* Description (if available) */}
            {area.description && (
              <p
                aria-describedby={`${areaId}-title`}
                className={cn(
                  TYPOGRAPHY.SMALL,
                  "text-default-600 line-clamp-2 mb-5 leading-relaxed",
                )}
              >
                {area.description.length > 120
                  ? `${area.description.substring(0, 120)}...`
                  : area.description}
              </p>
            )}

            {/* Footer with needs posted count */}
            <div
              className={cn(
                "flex items-center justify-between pt-4 border-t border-default-200 mt-auto",
              )}
            >
              <div className={cn("flex items-center", SPACING.BUTTON_GROUP)}>
                <div className="p-1.5 rounded-lg bg-primary-50">
                  <Heart
                    className={cn(COMPONENT_SIZES.ICON_SM, "text-primary-600")}
                  />
                </div>
                <span
                  className={cn(
                    TYPOGRAPHY.SMALL,
                    "font-semibold text-default-900",
                  )}
                >
                  {activeNeedsCount}{" "}
                  {formatNeedsLabel(activeNeedsCount, terminology)}
                </span>
              </div>
              {activeNeedsCount > 0 && (
                <span
                  className={cn(
                    TYPOGRAPHY.XS,
                    "font-medium text-primary-600 group-hover:text-primary-700 transition-colors",
                  )}
                >
                  View needs →
                </span>
              )}
            </div>
          </CardBody>
        </Card>
      </Link>
    </article>
  );
}
